import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { ApiError, errorMessageKey, unwrap } from '../../api/api-error';
import { ME_KEY } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { takeFragmentToken } from '../../lib/fragment-token';

const schema = z.object({ password: z.string().min(1, 'validation.required') });
type Values = z.infer<typeof schema>;

/**
 * Подтверждение email: ссылка из письма + пароль регистрации, и сразу вход. Пароль нужен,
 * потому что неподтверждённую регистрацию мог перезаписать посторонний (ADR-0013).
 */
export function VerifyEmailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token] = useState(takeFragmentToken);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { password: '' } });
  const verify = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(
        await api.POST('/api/v1/auth/email/verify', {
          body: { token: token ?? '', password: values.password },
        }),
      ),
    onSuccess: async (result) => {
      if (result.status === 'second_factor_required') {
        await navigate('/login/second-factor');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      await navigate('/', { replace: true });
    },
  });
  const mismatch =
    verify.error instanceof ApiError && verify.error.code === 'AUTH_VERIFY_PASSWORD_MISMATCH';

  if (token === null) {
    return (
      <AuthLayout title={t('auth.verify.title')}>
        <AuthError>{t('error.AUTH_TOKEN_INVALID')}</AuthError>
        <AuthLinks>
          <Link to="/login">{t('auth.toLogin')}</Link>
        </AuthLinks>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.verify.title')}>
      <p className="text-sm text-muted-foreground">{t('auth.verify.hint')}</p>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => verify.mutate(values))}
        noValidate
      >
        <TextField
          control={form.control}
          name="password"
          type="password"
          label={t('auth.field.password')}
          autoComplete="current-password"
          autoFocus
        />
        {verify.isError && <AuthError>{t(errorMessageKey(verify.error))}</AuthError>}
        <Button type="submit" className="w-full" disabled={verify.isPending}>
          {t('auth.verify.submit')}
        </Button>
      </form>
      {mismatch && (
        <AuthLinks>
          <Link to="/forgot-password">{t('auth.login.forgot')}</Link>
        </AuthLinks>
      )}
    </AuthLayout>
  );
}
